import React, { useRef, useEffect, useState, useCallback } from 'react';
import { socket } from '../socket';

import { Box, Typography, Tooltip, IconButton } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import MicIcon from '@mui/icons-material/Mic';

const AudioMonitor = ({ active, onAlertReceived, proctorPaused }) => {
  const [audioInitialized, setAudioInitialized] = useState(false);
  const [error, setError] = useState(null);
  const [micPermission, setMicPermission] = useState(null);
  const [calibrationMode, setCalibrationMode] = useState(false);
  const [calibrationData, setCalibrationData] = useState([]);
  const [adaptiveThreshold, setAdaptiveThreshold] = useState(0.15);
  const [audioLevels, setAudioLevels] = useState([]);
  const [showVisualizer, setShowVisualizer] = useState(true);
  
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const frequencyAnalyserRef = useRef(null);
  const micStreamRef = useRef(null);
  const requestAnimationRef = useRef(null);
  const lastAlertTimeRef = useRef(0);
  const lastThresholdExceededTimeRef = useRef(0);
  const lastChunkTimeRef = useRef(0);
  const baselineNoiseRef = useRef(0.05); // Initial baseline noise level
  const noiseHistoryRef = useRef([]); // Keep track of recent noise levels
  const spectralDataRef = useRef(null); // For frequency analysis
  const consecutiveDetectionsRef = useRef(0); // Track consecutive detections
  
  // Configuration parameters (optimized for slow/quiet speech detection)
  const BASE_VOLUME_THRESHOLD = 0.02; // Further decreased for higher sensitivity to quiet speech
  const NOISE_PERSISTENCE_TIME = 200; // Reduced for faster detection of slow speech
  const MINIMUM_ALERT_INTERVAL = 15000; // 15 seconds cooldown for voice warnings
  const BACKEND_CHUNK_INTERVAL = 2000; // Reduced for more frequent processing
  const CHUNK_DURATION = 1000; // Reduced for faster response to slow speech
  const FFT_SIZE = 2048; // Keep the same for good frequency resolution
  const SAMPLE_RATE = 16000; // Sample rate for backend audio processing
  const NOISE_HISTORY_LENGTH = 50; // Number of samples to keep for noise baseline
  const CALIBRATION_DURATION = 5000; // 5 seconds for calibration
  const FREQUENCY_BANDS = [
    { name: 'low', min: 20, max: 250 }, // Reduced upper limit for better speech isolation
    { name: 'speech', min: 250, max: 3500 }, // Extended range to catch more speech frequencies
    { name: 'high', min: 3500, max: 8000 } // Adjusted for better speech detection
  ];
  const SPEECH_DETECT_CONSECUTIVE_FRAMES = 1; // Only require 1 frame for faster detection
  const MIN_AMPLITUDE_FOR_DETECTION = 0.005; // Further decreased to detect very quiet/slow speech
  
  const stopAudioMonitoring = useCallback(() => {
    if (requestAnimationRef.current) {
      cancelAnimationFrame(requestAnimationRef.current);
      requestAnimationRef.current = null;
    }

    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(track => track.stop());
      micStreamRef.current = null;
    }

    if (audioContextRef.current) {
      if (audioContextRef.current.state !== 'closed') {
        try {
          audioContextRef.current.close();
        } catch (err) {
          console.error('Error closing AudioContext:', err);
        }
      }
      audioContextRef.current = null;
      analyserRef.current = null;
      frequencyAnalyserRef.current = null;
    }

    setAudioInitialized(false);
    consecutiveDetectionsRef.current = 0;
    setAudioLevels([]);
  }, []);

  // Calculate the adaptive threshold based on ambient noise
  const updateAdaptiveThreshold = useCallback(() => {
    const noiseHistory = noiseHistoryRef.current;
    if (noiseHistory.length > 0) {
      // Calculate the baseline as the 25th percentile of noise readings (adjusted from 20th)
      const sortedLevels = [...noiseHistory].sort((a, b) => a - b);
      const baselineIndex = Math.floor(sortedLevels.length * 0.25);
      const newBaseline = sortedLevels[baselineIndex];
      
      // Add a smaller margin to the baseline to get the threshold
      const newThreshold = Math.max(BASE_VOLUME_THRESHOLD, newBaseline * 1.3);
      
      // Smoothly adjust the threshold
      const currentThreshold = adaptiveThreshold;
      const adjustedThreshold = currentThreshold * 0.7 + newThreshold * 0.3;
      
      // Ensure the threshold isn't too low 
      const finalThreshold = Math.max(adjustedThreshold, BASE_VOLUME_THRESHOLD);
      
      setAdaptiveThreshold(finalThreshold);
      baselineNoiseRef.current = newBaseline;
      
      // Log for debugging
      if (Math.abs(finalThreshold - currentThreshold) > 0.01) {
        console.log(`Adaptive threshold updated: ${finalThreshold.toFixed(3)}, Baseline: ${newBaseline.toFixed(3)}`);
      }
    }
  }, [adaptiveThreshold]);

  // Analyze frequency bands to better detect speech vs. background noise
  const analyzeFrequencyBands = useCallback(() => {
    if (!frequencyAnalyserRef.current) return null;
    
    try {
      // Get frequency data
      const bufferLength = frequencyAnalyserRef.current.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      frequencyAnalyserRef.current.getByteFrequencyData(dataArray);
      
      // Calculate energy in different frequency bands
      const freqWidth = SAMPLE_RATE / 2 / bufferLength; // Width of each bin in Hz
      const bandResults = FREQUENCY_BANDS.map(band => {
        const minBin = Math.floor(band.min / freqWidth);
        const maxBin = Math.ceil(band.max / freqWidth);
        
        let sum = 0;
        for (let i = minBin; i < maxBin && i < bufferLength; i++) {
          sum += dataArray[i];
        }
        
        const average = sum / (maxBin - minBin);
        return { name: band.name, energy: average / 255 }; // Normalize to 0-1
      });
      
      // Calculate ratios to better identify speech
      const speechResult = bandResults.find(b => b.name === 'speech');
      const lowResult = bandResults.find(b => b.name === 'low');
      const highResult = bandResults.find(b => b.name === 'high');
      
      // Speech typically has higher energy in speech range than low and high combined
      const speechRatio = speechResult?.energy / (lowResult?.energy + highResult?.energy + 0.01);
      
      spectralDataRef.current = {
        bands: bandResults,
        speechRatio
      };
      
      return spectralDataRef.current;
    } catch (err) {
      console.error('Error analyzing frequency bands:', err);
      return null;
    }
  }, []);

  // Calibrate microphone for better noise detection
  const calibrateMicrophone = useCallback(() => {
    // Safety check - ensure analyzer is initialized
    if (!analyserRef.current) {
      console.warn("Cannot calibrate - analyser not initialized");
      setTimeout(() => {
        if (analyserRef.current) {
          console.log("Retrying calibration after delay");
          calibrateMicrophone();
        }
      }, 1000);
      return;
    }
    
    console.log("Starting microphone calibration");
    setCalibrationMode(true);
    setCalibrationData([]);
    
    const calibrationSamples = [];
    const takeCalibrationSample = () => {
      // Ensure analyzer still exists
      if (!analyserRef.current) {
        console.warn("Analyser reference lost during calibration");
        setCalibrationMode(false);
        return;
      }
      
      try {
        const bufferLength = analyserRef.current.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        analyserRef.current.getByteTimeDomainData(dataArray);
        
        // Calculate RMS (root mean square) volume
        let sum = 0;
        for (let i = 0; i < bufferLength; i++) {
          const amplitude = (dataArray[i] - 128) / 128;
          sum += amplitude * amplitude;
        }
        const rms = Math.sqrt(sum / bufferLength);
        
        calibrationSamples.push(rms);
        setCalibrationData(prev => [...prev, rms]);
        
        if (calibrationSamples.length < 20) {
          setTimeout(takeCalibrationSample, 250);
        } else {
          // Calculate baseline noise from calibration
          const sortedSamples = [...calibrationSamples].sort((a, b) => a - b);
          const baseline = sortedSamples[Math.floor(sortedSamples.length * 0.3)];
          baselineNoiseRef.current = baseline;

          // Set initial adaptive threshold (further lowered multiplier for slow speech)
          const newThreshold = Math.max(BASE_VOLUME_THRESHOLD, baseline * 1.1);
          setAdaptiveThreshold(newThreshold);
          
          console.log(`Calibration complete. Baseline noise: ${baseline.toFixed(3)}, Threshold: ${newThreshold.toFixed(3)}`);
          setCalibrationMode(false);
        }
      } catch (err) {
        console.error('Error during calibration:', err);
        setCalibrationMode(false);
      }
    };
    
    // Start calibration with a try-catch
    try {
      takeCalibrationSample();
    } catch (err) {
      console.error('Failed to start calibration:', err);
      setCalibrationMode(false);
    }
  }, []);

  // Send audio chunks to backend for validation
  const sendAudioChunkToBackend = useCallback(() => {
    if (!active || !audioContextRef.current || !micStreamRef.current) return;
    
    try {
      const now = Date.now();
      
      // Only send audio chunks at specified intervals
      if (now - lastChunkTimeRef.current < BACKEND_CHUNK_INTERVAL) {
        return;
      }
      
      lastChunkTimeRef.current = now;
      
      // Create a new recorder to capture audio for backend processing
      const recorder = new MediaRecorder(micStreamRef.current);
      const chunks = [];
      
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };
      
      recorder.onstop = () => {
        if (!chunks.length) return;
        
        // Create audio blob
        const blob = new Blob(chunks, { type: 'audio/webm' });
        
        // Convert to base64 for sending to server
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64data = reader.result.split(',')[1];
          
          // Send to server if socket is connected
          if (socket.connected) {
            socket.emit('audio_chunk', {
              audio: base64data,
              timestamp: new Date().toISOString(),
              // Send additional metadata to help backend analysis
              clientData: {
                adaptiveThreshold: adaptiveThreshold,
                baselineNoise: baselineNoiseRef.current,
                spectralData: spectralDataRef.current
              }
            });
          }
        };
        
        reader.readAsDataURL(blob);
      };
      
      // Start recording
      recorder.start();
      
      // Record for CHUNK_DURATION milliseconds
      setTimeout(() => {
        if (recorder.state === 'recording') {
          recorder.stop();
        }
      }, CHUNK_DURATION);
      
    } catch (err) {
      console.error('Error sending audio chunk:', err);
    }
  }, [active, adaptiveThreshold]);

  const initializeAudioMonitoring = useCallback(async () => {
    console.log("Initializing audio monitoring...");
    
    try {
      setError(null);
      
      // Stop any existing audio monitoring
      stopAudioMonitoring();

      // Request microphone access
      console.log("Requesting microphone access...");
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: SAMPLE_RATE
        }
      });

      micStreamRef.current = stream;
      setMicPermission(true);
      console.log("Microphone access granted");

      // Create audio context and analyzer
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContext({
        sampleRate: SAMPLE_RATE
      });
      audioContextRef.current = audioContext;

      // Create main amplitude analyzer
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = FFT_SIZE;
      analyserRef.current = analyser;
      
      // Create frequency analyzer
      const freqAnalyser = audioContext.createAnalyser();
      freqAnalyser.fftSize = FFT_SIZE;
      frequencyAnalyserRef.current = freqAnalyser;

      // Connect microphone to analyzers using splitter
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      source.connect(freqAnalyser);
      
      // Give a moment for audio context to initialize before starting analysis
      setTimeout(() => {
        // Start monitoring with safety check
        if (analyserRef.current && frequencyAnalyserRef.current) {
          startAudioAnalysis();
          // Wait a bit longer before calibration
          setTimeout(() => {
            // Additional check before calibration
            if (analyserRef.current) {
              calibrateMicrophone();
            }
          }, 1000);
        }
        setAudioInitialized(true);
      }, 500);
      
    } catch (err) {
      console.error("Error initializing audio:", err);
      setError(`Microphone error: ${err.message || "Unknown error"}`);
      
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        setMicPermission(false);
        setError("Microphone access denied. You must allow microphone access to take the exam.");
      } else if (err.name === "NotFoundError") {
        setMicPermission(false);
        setError("No microphone detected. Please connect a microphone to continue.");
      }
    }
  }, [stopAudioMonitoring, calibrateMicrophone]);

  const startAudioAnalysis = () => {
    // Extra safety check
    if (!analyserRef.current) {
      console.warn("Cannot start audio analysis - analyser not initialized");
      return;
    }
    
    const analyseAudio = () => {
      // If analyzers are no longer valid or proctoring is paused, cancel the loop
      if (!analyserRef.current || !active || proctorPaused) {
        if (requestAnimationRef.current) {
          cancelAnimationFrame(requestAnimationRef.current);
          requestAnimationRef.current = null;
        }
        return;
      }
      
      try {
        // Get time domain data for volume analysis
        const bufferLength = analyserRef.current.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        analyserRef.current.getByteTimeDomainData(dataArray);
        
        // Calculate RMS (root mean square) volume
        let sum = 0;
        for (let i = 0; i < bufferLength; i++) {
          // Convert from 0-255 to -1 to 1
          const amplitude = (dataArray[i] - 128) / 128;
          sum += amplitude * amplitude;
        }
        const rms = Math.sqrt(sum / bufferLength);
        
        // Update audio levels for visualization
        setAudioLevels(prevLevels => {
          const newLevels = [...prevLevels, rms];
          // Keep only the last 100 samples for visualization
          if (newLevels.length > 100) {
            return newLevels.slice(-100);
          }
          return newLevels;
        });
        
        // Add to noise history for adaptive thresholding
        noiseHistoryRef.current.push(rms);
        if (noiseHistoryRef.current.length > NOISE_HISTORY_LENGTH) {
          noiseHistoryRef.current.shift();
        }
        
        // Update adaptive threshold periodically (every 50 frames)
        if (noiseHistoryRef.current.length % 10 === 0) {
          updateAdaptiveThreshold();
        }
        
        // Analyze frequency content for better speech detection
        const freqData = analyzeFrequencyBands();
        
        // Skip processing if the volume is extremely low - set a very low noise floor check
        if (rms < MIN_AMPLITUDE_FOR_DETECTION) {
          // Reset counters for very low sounds
          consecutiveDetectionsRef.current = 0;
          lastThresholdExceededTimeRef.current = 0;
          
          // Continue the loop
          requestAnimationRef.current = requestAnimationFrame(analyseAudio);
          return;
        }
        
        // Determine if there is speech based on amplitude and spectral characteristics
        const amplitudeDetection = rms > adaptiveThreshold * 0.5; // Even more sensitive for slow speech
        // Further reduced ratio threshold for detecting quiet/slow speech
        const spectralDetection = freqData?.speechRatio > 0.6;

        // Combined detection for maximum sensitivity to slow speech
        const isDetected = amplitudeDetection || spectralDetection;
        
        if (isDetected) {
          consecutiveDetectionsRef.current++;
          const now = Date.now();
          
          // Mark the time when the threshold was first exceeded
          if (lastThresholdExceededTimeRef.current === 0) {
            lastThresholdExceededTimeRef.current = now;
          }
          
          // Check if noise has persisted long enough and we have enough consecutive detections
          const noiseDuration = now - lastThresholdExceededTimeRef.current;
          const timeSinceLastAlert = now - lastAlertTimeRef.current;
          const sufficientConsecutiveDetections = consecutiveDetectionsRef.current >= SPEECH_DETECT_CONSECUTIVE_FRAMES;
          
          if (noiseDuration >= NOISE_PERSISTENCE_TIME && 
              timeSinceLastAlert >= MINIMUM_ALERT_INTERVAL &&
              sufficientConsecutiveDetections) {
            
            // Extra verification with much lower threshold for slow speech
            if (rms < adaptiveThreshold * 0.3) {
              // If the sound is too quiet, skip this alert
              console.log("Sound level too low for alert, skipping");
              lastThresholdExceededTimeRef.current = 0;
              requestAnimationRef.current = requestAnimationFrame(analyseAudio);
              return;
            }

            // Very lenient speech detection for slow/quiet speech
            const isSpeech = spectralDetection || rms > adaptiveThreshold * 0.4;
            const alertType = isSpeech ? "SPEECH" : "NOISE";
            
            // Create alert with more detailed information
            const timestamp = new Date().toLocaleTimeString();
            
            // Use custom warning message for ALL audio detection (both speech and noise)
            let alertMessage = {
              type: "AUDIO",
              subType: alertType,
              message: `⚠️ Warning: Voice detected at ${timestamp}. You are not allowed to speak during the exam. Stay silent to avoid disqualification.`,
              timestamp: new Date().toISOString(),
              details: {
                amplitude: rms,
                threshold: adaptiveThreshold,
                speechRatio: freqData?.speechRatio || 0,
                consecutiveDetections: consecutiveDetectionsRef.current
              }
            };
            
            // Send to parent component
            onAlertReceived(alertMessage);
            
            // Optionally send to backend for validation/logging
            if (socket.connected) {
              socket.emit('audio_alert', alertMessage);
            }
            
            // Update last alert time
            lastAlertTimeRef.current = now;
            lastThresholdExceededTimeRef.current = 0;
            consecutiveDetectionsRef.current = 0;
            
            // Send an audio chunk for server-side validation
            sendAudioChunkToBackend();
          }
        } else {
          // Reset consecutive detections counter if no detection
          consecutiveDetectionsRef.current = 0;
          
          // Reset the persistence timer if sound drops below threshold
          lastThresholdExceededTimeRef.current = 0;
        }
        
        // Periodically send audio chunks to backend even without alerts
        sendAudioChunkToBackend();
      } catch (err) {
        console.error('Error in audio analysis:', err);
      }
      
      // Continue the loop
      requestAnimationRef.current = requestAnimationFrame(analyseAudio);
    };
    
    // Start the analysis loop with try-catch
    try {
      requestAnimationRef.current = requestAnimationFrame(analyseAudio);
    } catch (err) {
      console.error('Failed to start audio analysis:', err);
    }
  };

  // Set up socket event listeners for backend alerts
  useEffect(() => {
    if (!active) return;
    
    const handleAlert = (alertData) => {
      if (alertData.type === 'AUDIO') {
        // Use our custom warning message for ALL audio detection
        const timestamp = new Date().toLocaleTimeString();
        onAlertReceived({
          ...alertData,
          type: 'AUDIO',
          message: `⚠️ Warning: Voice detected at ${timestamp}. You are not allowed to speak during the exam. Stay silent to avoid disqualification.`
        });
      }
    };
    
    socket.on('alert', handleAlert);
    
    return () => {
      socket.off('alert', handleAlert);
    };
  }, [active, onAlertReceived]);

  // Start/stop audio monitoring based on active prop and proctorPaused state
  useEffect(() => {
    if (active && !audioInitialized && !proctorPaused) {
      console.log("Exam active - initializing audio monitoring");
      initializeAudioMonitoring();
    }
    
    // Clean up when exam becomes inactive or proctoring is paused
    if ((!active || proctorPaused) && audioInitialized) {
      console.log("Exam inactive or proctoring paused - stopping audio monitoring");
      stopAudioMonitoring();
    }
    
    return () => {
      // Clean up on component unmount
      if (audioInitialized) {
        stopAudioMonitoring();
      }
    };
  }, [active, audioInitialized, initializeAudioMonitoring, stopAudioMonitoring, proctorPaused]);

  // If not visible or not active, return null
  if (!active) return null;
  
  // Return an invisible component that still functions
  return (
    <Box sx={{ 
      position: 'absolute',
      width: 0,
      height: 0,
      overflow: 'hidden',
      visibility: 'hidden'
    }}>

      {calibrationMode && (
        <Typography variant="caption" color="primary">
          Calibrating microphone...
        </Typography>
      )}
    </Box>
  );
};

export default AudioMonitor;